import { useCallback } from "react";
import type { ClientContext } from "@maintainer-pro/ai-cli";

export type BuildClientContextInput = {
  route?: string;
  pageUrl?: string;
  pageTitle?: string;
  visiblePanels?: string[];
  relevantFiles?: string[];
  data?: Record<string, unknown>;
};

type PageLocation = {
  href: string;
  pathname: string;
  search: string;
  hash: string;
};

/** Strip `/p/{token}/{app}` so the model sees the host app path. */
export function appRouteFromPathname(
  pathname: string,
  search = "",
  hash = ""
): string {
  const match = pathname.match(/^\/p\/[^/]+\/[^/]+(\/.*)?$/);
  const path = match ? match[1] || "/" : pathname || "/";
  return `${path}${search}${hash}`;
}

function readLocation(win: Window): PageLocation | null {
  try {
    const loc = win.location;
    if (!loc?.href) return null;
    return {
      href: loc.href,
      pathname: loc.pathname || "/",
      search: loc.search || "",
      hash: loc.hash || "",
    };
  } catch {
    return null;
  }
}

function isAiSidecarPath(pathname: string): boolean {
  return /\/p\/[^/]+\/ai(?:\/|$)/.test(pathname);
}

function frameworkRoute(): string | undefined {
  try {
    const router = (
      window as Window & {
        next?: { router?: { asPath?: string; pathname?: string } };
      }
    ).next?.router;
    const asPath = router?.asPath?.trim();
    if (asPath) return asPath;
    const pathname = router?.pathname?.trim();
    if (pathname && pathname !== "/_error") return pathname;
  } catch {
    // ignore
  }
  return undefined;
}

function pickLocation(
  self: PageLocation | null,
  parent: PageLocation | null,
  top: PageLocation | null
): PageLocation | null {
  const candidates = [top, parent, self].filter(
    (row): row is PageLocation => Boolean(row)
  );
  return (
    candidates.find((row) => !isAiSidecarPath(row.pathname)) ??
    self ??
    parent ??
    top
  );
}

/** Current browser page — top/parent when the widget is framed. */
export function readOpenPage(): {
  pageUrl: string;
  route: string;
  pageTitle: string;
} | null {
  if (typeof window === "undefined") return null;
  let topLoc: PageLocation | null = null;
  let parentLoc: PageLocation | null = null;
  try {
    if (window.top && window.top !== window) topLoc = readLocation(window.top);
  } catch {
    // cross-origin
  }
  try {
    if (window.parent && window.parent !== window) {
      parentLoc = readLocation(window.parent);
    }
  } catch {
    // cross-origin
  }
  const loc = pickLocation(readLocation(window), parentLoc, topLoc);
  if (!loc) return null;
  const fromLocation = appRouteFromPathname(loc.pathname, loc.search, loc.hash);
  const fromFramework = frameworkRoute();
  const route =
    fromLocation === "/" && fromFramework && fromFramework !== "/"
      ? fromFramework
      : fromLocation;
  let pageTitle = "App";
  try {
    if (document.title.trim()) pageTitle = document.title.trim();
  } catch {
    // ignore
  }
  if (topLoc && window.top && window.top !== window) {
    try {
      const topTitle = window.top.document?.title?.trim();
      if (topTitle) pageTitle = topTitle;
    } catch {
      // ignore
    }
  }
  return { pageUrl: loc.href, route, pageTitle };
}

function focusedElementLabel(): string | null {
  if (typeof document === "undefined") return null;
  try {
    const el = document.activeElement;
    if (!(el instanceof HTMLElement)) return null;
    return (
      el.innerText?.slice(0, 80) ||
      el.getAttribute("aria-label") ||
      el.tagName.toLowerCase()
    );
  } catch {
    return null;
  }
}

/** Helper to assemble a ClientContext from browser + host data. */
export function buildBrowserClientContext(
  input: BuildClientContextInput = {}
): ClientContext {
  const page = readOpenPage();
  const href =
    input.pageUrl ??
    page?.pageUrl ??
    (typeof window !== "undefined" ? window.location.href : undefined);
  const route =
    input.route ??
    page?.route ??
    (typeof window !== "undefined"
      ? appRouteFromPathname(
          window.location.pathname,
          window.location.search,
          window.location.hash
        )
      : "/");
  const pageTitle =
    input.pageTitle ??
    page?.pageTitle ??
    (typeof document !== "undefined" ? document.title || "App" : "App");

  return {
    route,
    pageUrl: href,
    pageTitle,
    visiblePanels: input.visiblePanels,
    focusedElement: focusedElementLabel(),
    relevantFiles: input.relevantFiles,
    data: input.data,
  };
}

export function useClientContextBuilder(
  getInput: () => BuildClientContextInput
): () => ClientContext {
  return useCallback(() => buildBrowserClientContext(getInput()), [getInput]);
}
